import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHero, SectionHead, CTABand } from "@/components/PageHero";
import { Reveal } from "@/components/anim";
import { IMG } from "@/lib/img";

const OFFERS = [
  { name: "1-Day Pop-Up", desc: "A single day of in-person selling — perfect for launches and test runs." },
  { name: "Weekend Takeover", desc: "Friday through Sunday, the busiest shopping window of the week." },
  { name: "Weekly Retail Rental", desc: "Seven days to build momentum and meet your customers." },
  { name: "Monthly Retail Residency", desc: "A 30-day storefront to grow a real, returning customer base." },
  { name: "Product Launch Rental", desc: "A curated setting to unveil a collection or drop." },
  { name: "Private Shopping Event", desc: "An exclusive, after-hours shopping experience for your people." },
];

const USES = [
  "Launches",
  "Seasonal collections",
  "Fittings",
  "Customer meet-and-greets",
  "Shopping events",
  "Brand activations",
  "Temporary storefronts",
];

const INCLUDED = [
  "Clothing racks",
  "Shelving",
  "Mirrors",
  "Checkout area",
  "Wi-Fi",
  "Lighting",
  "Fitting area",
  "Display furniture",
  "Basic signage areas",
];

export default function PopUpShop() {
  return (
    <>
      <PageHero
        eyebrow="Pop-Up Shop"
        lines={[
          <>From Followers</>,
          <>
            To <em>Foot Traffic.</em>
          </>,
        ]}
        sub="Online businesses can temporarily operate from Essandtials without having to sign a traditional commercial lease. Bring your followers to a real front door — then take the sales home with you."
      />

      <section className="bg-paper pb-24 sm:pb-32" data-testid="popup-offers">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {OFFERS.map((o, i) => (
              <Reveal key={o.name} delay={i * 0.06}>
                <div className="group flex h-full flex-col justify-between rounded-3xl border border-beige/60 bg-white/80 p-8 shadow-[0_4px_24px_rgba(26,24,23,0.04)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(26,24,23,0.1)]" data-testid={`popup-offer-${i + 1}`}>
                  <p className="font-serif text-sm italic text-sanddeep">0{i + 1}</p>
                  <div>
                    <h3 className="mt-6 font-serif text-2xl uppercase text-ink">{o.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-clay">{o.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand py-24 sm:py-32" data-testid="popup-uses">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
          <div>
            <SectionHead
              eyebrow="Use it your way"
              title={
                <>
                  Made for
                  <br />
                  <em>the moment.</em>
                </>
              }
              sub="The space can be used for launches, seasonal collections, fittings, customer meet-and-greets, shopping events, brand activations, and temporary storefronts."
            />
            <Reveal delay={0.3}>
              <ul className="mt-8 flex flex-wrap gap-2.5">
                {USES.map((u) => (
                  <li key={u} className="chip">
                    {u}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <img
              src={IMG.store.counter}
              alt="Inside Essandtials — marble checkout counter and gold display tables"
              loading="lazy"
              className="aspect-[4/5] w-full rounded-3xl object-cover shadow-[0_30px_80px_rgba(26,24,23,0.15)]"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-paper py-24 sm:py-32" data-testid="popup-included">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionHead
            eyebrow="Everything is here"
            title={
              <>
                What&rsquo;s <em>included.</em>
              </>
            }
          />
          <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {INCLUDED.map((item, i) => (
              <Reveal key={item} delay={i * 0.04}>
                <div className="flex items-center gap-3 rounded-2xl border border-beige/60 bg-white/70 px-5 py-4">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blush">
                    <Check size={13} strokeWidth={2} className="text-ink" />
                  </span>
                  <span className="text-sm text-ink">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title={
          <>
            Your followers are waiting.
            <br />
            <em>Give them a door to walk through.</em>
          </>
        }
        cta="Book Your Pop-Up"
        to="/book?type=Pop-Up%20Shop"
        testid="popup-book-button"
      />
    </>
  );
}
